import { z } from 'zod';
import type { Order } from '../order/orderSchema';
import { assertTypes, type NoNewFields } from '../primitives';

/**
 * One menu item's stock at one restaurant: a unit count, a staff "sold out" mark, or both. Rows
 * only exist for items staff have touched; no row = available and not counted.
 *
 * Its own table, not part of the menu, so a per-sale write never snapshots a `menuVersions` row.
 * Deleting a menu item deliberately doesn't cascade here: an orphan is invisible (reads join by
 * uuid), the menu stays unaware of this module, and restoring a menu version brings the count back.
 *
 * Turning the `menu-item-stock` flag off deletes the venue's rows: nothing counts sales while it's
 * off, so the counts would be wrong when it's turned back on.
 *
 * `qty` is signed and never floored: a POS sells offline and syncs later, so the server can't
 * refuse the sale, and flooring would break the order ledger (a later cancel would release units
 * never subtracted).
 *
 * `countedAt` guards the same ledger. Staff set a count from what is on hand, so an order saved
 * before it holds none of its units: cancelling, voiding or editing it down releases nothing.
 */
export const menuItemStockSchema = z.object({
  restaurantId: z.string(),
  menuItemUuid: z.string(),
  /** Units on hand, or `null` when not counted. Can go negative; clients show `max(0, qty)`. */
  qty: z.number().nullable().default(null),
  /** Staff marked it sold out, whatever the count says. */
  isSoldOut: z.boolean().default(false),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  /** When staff last changed `qty`. A sale or a sold out mark doesn't restart it. */
  countedAt: z.number().optional(),
  updatedAt: z.number().optional(),
});
export type MenuItemStock = z.output<typeof menuItemStockSchema>;

/** Strict schema */
export const menuItemStockStrictSchema = menuItemStockSchema.safeExtend({
  menuItemUuid: z.guid(),
  qty: z.int().nullable(),
});

/** Convex schema */
export const menuItemStockCvxSchema = menuItemStockSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  countedAt: z.number(),
  updatedAt: z.number(),
});
export type MenuItemStockCvx = z.output<typeof menuItemStockCvxSchema>;
assertTypes<NoNewFields<MenuItemStock, MenuItemStockCvx>>();

/** A restaurant's stock rows keyed by `menuItemUuid`. */
export const menuItemStockByMenuItemUuidSchema = z.record(z.string(), menuItemStockCvxSchema);
export type MenuItemStockByMenuItemUuid = z.output<typeof menuItemStockByMenuItemUuidSchema>;

/** Units per `menuItemUuid`. */
export type QtyByMenuItemUuid = Record<string, number>;

/** Sold out = counted down to zero or below, or marked by staff. */
export const isMenuItemSoldOut = (stock: MenuItemStock | undefined): boolean =>
  stock !== undefined && ((stock.qty !== null && stock.qty <= 0) || stock.isSoldOut);

/**
 * Units the order holds, summed per item. Cancelled orders and voided lines hold nothing. A
 * SUBMITTED online order holds its units until it is rejected.
 */
export const getMenuItemStockDemand = (
  order: Pick<Order, 'orderItems' | 'status'>,
): QtyByMenuItemUuid => {
  if (order.status === 'CANCELLED') return {};
  const demand: QtyByMenuItemUuid = {};
  for (const orderItem of order.orderItems) {
    if (orderItem.voidedAt !== null) continue;
    demand[orderItem.menuItemUuid] = (demand[orderItem.menuItemUuid] ?? 0) + orderItem.qty;
  }
  return demand;
};

/** `next − prev` per item, zeros dropped. Positive = consumed, negative = released. */
export const getMenuItemStockDelta = (
  prev: QtyByMenuItemUuid,
  next: QtyByMenuItemUuid,
): QtyByMenuItemUuid => {
  const delta: QtyByMenuItemUuid = {};
  for (const uuid of new Set([...Object.keys(prev), ...Object.keys(next)])) {
    const change = (next[uuid] ?? 0) - (prev[uuid] ?? 0);
    if (change !== 0) delta[uuid] = change;
  }
  return delta;
};

/** Items the demand can't be met for: sold out, or counted and short. Untracked items pass. */
export const getMenuItemUuidsNotInStock = (
  demand: QtyByMenuItemUuid,
  stockByUuid: MenuItemStockByMenuItemUuid,
): string[] =>
  Object.entries(demand)
    .filter(([uuid, demanded]) => {
      const stock = stockByUuid[uuid];
      if (!stock) return false;
      return isMenuItemSoldOut(stock) || (stock.qty !== null && demanded > stock.qty);
    })
    .map(([uuid]) => uuid);
